interface RuntimeConfig {
  REACT_APP_API_URL: string;
  REACT_APP_UMBRACO_API_URL: string;
  REACT_APP_GOOGLE_CLIENT_ID: string;
  REACT_APP_GOOGLE_REDIRECT_URI: string;
  REACT_APP_SITE_PATH: string;
  REACT_APP_SITE_NAME: string;
  REACT_APP_THEME: string;
  REACT_APP_IS_DEMO: string;
  REACT_APP_PWA_ENABLED: string;
  REACT_APP_GTM_ID: string;
}

declare global {
  interface Window {
    __RUNTIME_CONFIG__?: Partial<RuntimeConfig>;
  }
}

function getConfig(key: keyof RuntimeConfig, fallback: string = ''): string {
  const runtimeValue = window.__RUNTIME_CONFIG__?.[key];
  if (runtimeValue) return runtimeValue;

  const envValue = process.env[key];
  if (envValue) return envValue;

  return fallback;
}

/** Resolve media URLs: absolute URLs pass through, relative paths get the Umbraco API host prepended. */
export function mediaUrl(url: string | undefined): string | undefined {
  if (!url) return undefined;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  const base = config.umbracoApiUrl.replace(/\/$/, '');
  return `${base}${url.startsWith('/') ? '' : '/'}${url}`;
}

/**
 * Rewrites Unsplash URLs to request smaller, better-compressed images.
 * Strips any existing params and applies `auto=format&fit=crop&q=70&w={targetWidth}`.
 * Non-Unsplash URLs pass through unchanged.
 */
export function optimizeImageUrl(url: string | undefined, targetWidth = 700): string | undefined {
  if (!url) return url;
  if (!url.includes('images.unsplash.com/')) return url;
  const [base] = url.split('?');
  return `${base}?auto=format&fit=crop&q=70&w=${targetWidth}`;
}

/**
 * Rewrites Unsplash `<img>` URLs inside an HTML string and appends lazy-loading
 * + aspect-ratio hints (if not already present) so the browser can reserve space.
 */
export function optimizeHtmlImages(html: string, targetWidth = 700): string {
  return html.replace(
    /<img\b([^>]*?)src=(["'])(https:\/\/images\.unsplash\.com\/[^"']+)\2([^>]*)>/gi,
    (_match, before, quote, src, after) => {
      const attrs = before + after;
      const hasLazy = /\bloading=/i.test(attrs);
      const hasDecoding = /\bdecoding=/i.test(attrs);
      const hasWidth = /\bwidth=/i.test(attrs);
      const hasHeight = /\bheight=/i.test(attrs);
      const extra =
        (hasLazy ? '' : ' loading="lazy"') +
        (hasDecoding ? '' : ' decoding="async"') +
        (hasWidth ? '' : ` width="${targetWidth}"`) +
        (hasHeight ? '' : ` height="${Math.round(targetWidth * 9 / 16)}"`);
      return `<img${before}src=${quote}${optimizeImageUrl(src, targetWidth)}${quote}${after}${extra}>`;
    }
  );
}

export const config = {
  get apiUrl(): string {
    return getConfig('REACT_APP_API_URL', 'http://localhost:5100');
  },
  get umbracoApiUrl(): string {
    return getConfig('REACT_APP_UMBRACO_API_URL', 'http://localhost:5000');
  },
  get googleClientId(): string {
    return getConfig('REACT_APP_GOOGLE_CLIENT_ID');
  },
  get googleRedirectUri(): string {
    return getConfig('REACT_APP_GOOGLE_REDIRECT_URI');
  },
  get sitePath(): string {
    return getConfig('REACT_APP_SITE_PATH');
  },
  get siteName(): string {
    return getConfig('REACT_APP_SITE_NAME', 'Rezerwacje Online');
  },
  get theme(): string {
    return getConfig('REACT_APP_THEME');
  },
  get isDemo(): boolean {
    return getConfig('REACT_APP_IS_DEMO', 'true') === 'true';
  },
  get pwaEnabled(): boolean {
    return getConfig('REACT_APP_PWA_ENABLED', 'false') === 'true';
  },
  get gtmId(): string {
    return getConfig('REACT_APP_GTM_ID');
  },
};
